import { ChangeDetectorRef, Component, EventEmitter, forwardRef, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { SettingsService, SvcUserAvatarService, SvcUserInfo } from 'projects/lib-shared-core/src/public-api';
import { ISvcChipGroupedOption, ISvcChipOption } from '@solvace/component/svc-controls';
import { ISvcUserAvatar } from '@solvace/component/svc-user-avatar';
import { getCustomThemeColor, SvcTestIdDirective } from 'projects/lib-shared-common/src/public-api';
import { SvcControl } from '@solvace/component/svc-controls';

@Component({
  selector: 'svc-user-picker',
  templateUrl: './svc-user-picker.component.html',
  styleUrls: ['./svc-user-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SvcUserPickerComponent),
      multi: true,
    },
  ],
})
export class SvcUserPickerComponent extends SvcControl implements OnInit, OnChanges, OnDestroy {
  @Input() public formControl: FormControl;
  @Input('ids') public set setIds(value: string | string[]) {
    value = value ?? [];
    let ids = Array.from(new Set(typeof value === 'string' ? [value] : value)).filter((v) => !!v) ?? [];
    if (JSON.stringify(ids) !== JSON.stringify(this.ids)) {
      this.ids = ids;
      this.length = this.ids.length;
      this.#loadSvcUserInfo();
    }
  }
  @Input() label: string | { text: string; icon: string };
  @Input() icon: string;
  @Input() type: 'single' | 'multiple' = 'multiple';
  @Input() readonly: boolean = false;
  @Input() disabled: boolean = false;
  @Input() inputId: string;
  @Input() placeholder: string;
  @Input() fallbackTextValue: string = '-';
  @Input() loading: boolean = false;
  @Output() public onLoadingChange = new EventEmitter<boolean>();

  #service = inject(SvcUserAvatarService);
  // Default-CD component that may render under an OnPush host; its own user lookup mutates plain
  // fields, so mark for check when it resolves or the picker stays on its loading state until an
  // unrelated click ticks change detection (AB#69059).
  #cdr = inject(ChangeDetectorRef);
  #requestSubscription: Subscription;
  #onInitWasCalled = false;
  #fallbackInitialColors = getCustomThemeColor(inject(SettingsService).theme$, 500);
  // Host `svcTestId` is forwarded to the inner <svc-chip> input so automation targets the real control, not the wrapper.
  #svcTestId = this.#claimHostTestId();

  #claimHostTestId(): SvcTestIdDirective | null {
    const directive = inject(SvcTestIdDirective, { self: true, optional: true });
    if (directive) directive.useOnHost = false;
    return directive;
  }

  public get inputTestId(): string | null {
    return this.#svcTestId?.normalizedTestId || null;
  }

  public isLoading: boolean = false;
  protected length: number = 0;
  protected ids: string[] = [];
  protected chipOptions: ISvcChipOption[] = [];

  public ngOnInit(): void {
    super.ngOnInit();
    this.#onInitWasCalled = true;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    // eslint-disable-next-line no-empty
    if (this.#onInitWasCalled) {
    }

    if ('disabled' in changes) {
      try {
        if (this.formControl) {
          if (this.disabled) {
            this.formControl.disable({ emitEvent: false });
          } else {
            this.formControl.enable({ emitEvent: false });
          }
        } else if (this.control) {
          if (this.disabled) {
            this.control.disable({ emitEvent: false });
          } else {
            this.control.enable({ emitEvent: false });
          }
        }
      } catch (e) {}
    }
  }

  public refresh() {
    !this.isLoading && this.#loadSvcUserInfo();
  }

  #loadSvcUserInfo() {
    this.#requestSubscription?.unsubscribe();
    if (this.length > 0) {
      this.isLoading = true;
      this.#requestSubscription = this.#service
        .getUserInfoByUserIds(this.ids)
        .pipe(
          catchError(() => of([])),
          tap((res) => this.#mountChipOption(res)),
          finalize(() => {
            this.#requestSubscription = null;
            this.isLoading = false;
            this.#cdr.markForCheck();
          })
        )
        .subscribe();
    } else {
      this.isLoading = false;
      this.chipOptions = [];
      this.#cdr.markForCheck();
    }
  }

  #mountChipOption(usersInfo: SvcUserInfo[]) {
    this.chipOptions = usersInfo.map((ui) => ({
      label: ui.name,
      value: ui.id,
      data: <ISvcUserAvatar>{
        name: ui.name,
        picture: ui.pictureFile,
        initialsColor: (ui.initialColor ?? '') !== '' ? ui.initialColor : this.#fallbackInitialColors,
        type: ui.type,
        size: 24,
      },
    }));
  }

  ngOnDestroy(): void {
    this.#requestSubscription?.unsubscribe();
  }
}
